import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Sword, Skull, Heart, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "State Machines",
        icon: "🧠",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">AI Brains</h3>
                    <p className="text-slate-700 mb-4">
                        How does a game enemy know when to attack or run away?
                        It uses a <strong>Finite State Machine (FSM)</strong>.
                    </p>
                    <p className="text-slate-700">
                        Think of it like a traffic light. It can only be Red, Yellow, or Green. It can't be Red AND Green at the same time!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The States",
        icon: "🚦",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg">Our Boss has 3 states:</p>
                <div className="space-y-4">
                    <div className="flex items-center gap-4 bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="font-bold text-blue-800 w-24">1. IDLE</div>
                        <div className="text-sm text-slate-600">Waiting for cooldown. Safe to watch.</div>
                    </div>
                    <div className="flex items-center gap-4 bg-red-50 p-4 rounded-xl border border-red-200">
                        <div className="font-bold text-red-800 w-24">2. ATTACK</div>
                        <div className="text-sm text-slate-600">Dealing damage! Don't get hit!</div>
                    </div>
                    <div className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="font-bold text-green-800 w-24">3. VULNERABLE</div>
                        <div className="text-sm text-slate-600">Tired after attacking. <strong>HIT HIM NOW!</strong></div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "The Logic Loop",
        icon: "🔄",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-800 p-6 rounded-2xl text-white font-mono text-sm leading-loose">
                    <div><span className="text-purple-400">switch</span> (bossState) {'{'}</div>
                    <div className="pl-4">
                        <span className="text-purple-400">case</span> <span className="text-yellow-400">'IDLE'</span>:
                        <span className="text-slate-400"> // Wait 2s, then Attack</span>
                    </div>
                    <div className="pl-4">
                        <span className="text-purple-400">case</span> <span className="text-red-400">'ATTACK'</span>:
                        <span className="text-slate-400"> // Hit player, then Rest</span>
                    </div>
                    <div className="pl-4">
                        <span className="text-purple-400">case</span> <span className="text-green-400">'VULNERABLE'</span>:
                        <span className="text-slate-400"> // Take damage!</span>
                    </div>
                    <div>{'}'}</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">🎮 Mission: Defeat the Boss</h4>
                    <p className="text-blue-700 text-sm">
                        Wait for the <strong>VULNERABLE</strong> state, then strike!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
type BossState = 'IDLE' | 'ATTACK' | 'VULNERABLE' | 'DEFEATED';

const BossGame = ({ onComplete }: { onComplete: () => void }) => {
    // Boss Stats
    const [bossState, setBossState] = useState<BossState>('IDLE');
    const [hp, setHp] = useState(100);
    const [message, setMessage] = useState("Boss is observing you...");

    // Game Loop (The State Machine)
    useEffect(() => {
        if (bossState === 'DEFEATED') return;

        let timer: NodeJS.Timeout;

        switch (bossState) {
            case 'IDLE':
                setMessage("Boss is waiting...");
                timer = setTimeout(() => {
                    setBossState('ATTACK');
                }, 2000);
                break;
            case 'ATTACK':
                setMessage("BOSS ATTACKS! DODGE!");
                timer = setTimeout(() => {
                    setBossState('VULNERABLE');
                }, 1500); // Attack duration
                break;
            case 'VULNERABLE':
                setMessage("Boss is tired! ATTACK NOW!");
                timer = setTimeout(() => {
                    setBossState('IDLE');
                }, 2000); // Vulnerable window
                break;
        }

        return () => clearTimeout(timer);
    }, [bossState]);

    const handlePlayerAttack = () => {
        if (bossState === 'DEFEATED') return;

        if (bossState === 'VULNERABLE') {
            const damage = 20;
            const newHp = Math.max(0, hp - damage);
            setHp(newHp);
            setMessage("CRITICAL HIT! -20 HP");

            if (newHp === 0) {
                setBossState('DEFEATED');
                onComplete();
            }
        } else {
            setMessage("ATTACK BLOCKED! Wait for an opening!");
        }
    };

    return (
        <div className="max-w-4xl mx-auto text-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

                {/* Boss Visualizer */}
                <div className="flex flex-col items-center gap-6">
                    {/* HP Bar */}
                    <div className="w-full h-8 bg-slate-800 rounded-full border-2 border-slate-700 overflow-hidden relative">
                        <motion.div
                            className="h-full bg-gradient-to-r from-red-600 to-red-500"
                            initial={{ width: '100%' }}
                            animate={{ width: `${hp}%` }}
                            transition={{ type: 'spring' }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center font-bold text-xs">
                            {hp} / 100 HP
                        </div>
                    </div>

                    {/* The Boss */}
                    <div className="relative mt-12 mb-4">
                        {/* State Indicator */}
                        <div className={`absolute -top-12 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest border shadow-lg ${bossState === 'IDLE' ? 'bg-blue-500 border-blue-400' :
                            bossState === 'ATTACK' ? 'bg-red-500 border-red-400 animate-pulse' :
                                bossState === 'VULNERABLE' ? 'bg-green-500 border-green-400' :
                                    'bg-slate-500 border-slate-400'
                            }`}>
                            {bossState}
                        </div>

                        <motion.div
                            animate={
                                bossState === 'IDLE' ? { scale: 1, rotate: 0 } :
                                    bossState === 'ATTACK' ? { scale: 1.2, x: [0, -10, 10, -10, 10, 0], color: '#ef4444' } :
                                        bossState === 'VULNERABLE' ? { scale: 0.9, rotate: [0, 5, -5, 5, -5, 0], color: '#22c55e' } :
                                            { scale: 0, opacity: 0 }
                            }
                            transition={{ duration: 0.5 }}
                            className={`w-48 h-48 rounded-3xl flex items-center justify-center shadow-2xl border-4 ${bossState === 'IDLE' ? 'bg-slate-800 border-slate-600' :
                                bossState === 'ATTACK' ? 'bg-red-900 border-red-500' :
                                    bossState === 'VULNERABLE' ? 'bg-green-900 border-green-500' :
                                        'bg-slate-900'
                                }`}
                        >
                            {bossState === 'ATTACK' ? <Skull size={80} className="text-red-500" /> :
                                bossState === 'VULNERABLE' ? <Heart size={80} className="text-green-500" /> :
                                    <Shield size={80} className="text-slate-400" />
                            }
                        </motion.div>
                    </div>

                    <motion.p
                        key={message}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center font-mono text-sm h-6 text-yellow-400"
                    >
                        {message}
                    </motion.p>
                </div>

                {/* Controls */}
                <div className="space-y-6">
                    <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Sword className="text-blue-400" /> Player Actions
                        </h3>
                        <button
                            onClick={handlePlayerAttack}
                            className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3 border-b-4 border-blue-800 active:border-b-0 active:translate-y-1"
                        >
                            <Sword size={24} /> ATTACK!
                        </button>
                        <p className="text-xs text-slate-500 text-center mt-3">
                            Tip: Attack only when the shield is down (Vulnerable).
                        </p>
                    </div>

                    <div className="bg-purple-900/20 p-6 rounded-2xl border border-purple-500/30">
                        <h3 className="font-bold text-purple-300 mb-2">State Machine Logic</h3>
                        <div className="font-mono text-xs bg-slate-950 p-3 rounded text-slate-400 space-y-1">
                            <div className={bossState === 'IDLE' ? 'text-blue-400 font-bold' : ''}>1. IDLE (Wait 2s)</div>
                            <div className="pl-4">↓</div>
                            <div className={bossState === 'ATTACK' ? 'text-red-400 font-bold' : ''}>2. ATTACK (Hit Player)</div>
                            <div className="pl-4">↓</div>
                            <div className={bossState === 'VULNERABLE' ? 'text-green-400 font-bold' : ''}>3. VULNERABLE (Take Dmg)</div>
                            <div className="pl-4">↓ (Loop)</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const BossLogic = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('boss-logic', 100);
    };

    return (
        <LessonWrapper
            moduleName="Boss Logic"
            moduleEmoji="🧠"
            moduleColor="purple"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Boss Fight"
        >
            <BossGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default BossLogic;
